import { NgClass, SlicePipe } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  runInInjectionContext,
  type Signal,
  signal,
  Type,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { ToolInvocation } from '@ai-sdk/ui-utils';
import { marked } from 'marked';

import { createProviderToken, FnCallPipe, RtComponentOutlet } from '@core/common-legacy';
import { type InfoboxButton, InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { HrAssistantAnalyticsService } from '@core/intelligence-logic';
import { HrAssistantIconComponent, ThumbUpDownFeedbackComponent } from '@core/intelligence-ui';
import { type AnySymbol, SymbolComponent } from '@core/symbol-legacy';
import {
  AvatarComponent,
  ButtonComponent,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  RtTooltip,
  TextareaComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';

import { HeaderThreadComponent } from './header-thread/header-thread.component';
import { HeaderWelcomeComponent } from './header-welcome/header-welcome.component';
import { type AiMessage, HrAssistantChatService } from './hr-assistant-chat.service';

interface VectorQueryRelevantContext {
  connection_id: string;
  id: string;
  org_guid: string;
  provider_id: 'notion' | string;
  text: string;
  title: string;
  url: string;
}

interface VectorQuerySource {
  document: string;
  id: string;
  metadata: VectorQueryRelevantContext;
  score: number; // 0 - 1
  vector: unknown[];
}

interface VectorQueryToolResult {
  relevantContext: VectorQueryRelevantContext[];
  sources: VectorQuerySource[];
}

interface AiResponseContext {
  title: string;
  url: string;
  text: string;
  icon: string;
}

export interface ConversationStarter {
  content: string;
  symbol: AnySymbol | null;
}

export interface HRAssistantFeature {
  id: string;
  welcomeCmp: Type<any>;
  // Runs in the injection context
  welcomeCmpVisible?: () => Signal<boolean>;
  activateCmp: Type<any>;
}

export const { provide: provideHrAssistantFeature, inject: injectHrAssistantFeatures } =
  createProviderToken<HRAssistantFeature>('HRAssistantFeatures')(true);

const SOURCE_SCORE_THRESHOLD = 0.5;

@Component({
  selector: 'hr-assistant-chat',
  imports: [
    TextareaComponent,
    ReactiveFormsModule,
    NgClass,
    IconButtonComponent,
    HrAssistantIconComponent,
    ButtonComponent,
    IconComponent,
    HeadingComponent,
    DividerComponent,
    AvatarComponent,
    SymbolComponent,
    FnCallPipe,
    RtTooltip,
    SlicePipe,
    TruncateComponent,
    ThumbUpDownFeedbackComponent,
    TranslatePipe,
    InfoboxComponent,
    RtComponentOutlet,
    HeaderWelcomeComponent,
    HeaderThreadComponent,
  ],
  templateUrl: './hr-assistant-chat.component.html',
  styleUrls: ['./hr-assistant-chat.component.less'],
})
export class HrAssistantChatComponent {
  private readonly injector = inject(Injector);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hrAssistantChatService = inject(HrAssistantChatService);
  private readonly hrAssistantAnalytics = inject(HrAssistantAnalyticsService);
  private readonly messagesContainer = viewChild<ElementRef<HTMLElement>>('messagesContainer');
  private readonly features = injectHrAssistantFeatures({ optional: true }) ?? [];

  protected readonly DEFAULT_MESSAGE_CONTEXT_COUNT = 3;

  public conversationStarters = input<ConversationStarter[]>([]);
  public greeting = input<string>('');
  public userAvatar = input<string | null>(null);
  public userInitials = input<string>('');
  public userFullName = input.required<string>();

  public close = output<void>();

  public contextExpandedMap = signal<Record<string, boolean>>({});
  public threads = this.hrAssistantChatService.threads;
  public currentThreadId = this.hrAssistantChatService.currentThreadId;
  public chatForm = new FormControl('', { nonNullable: true });

  protected availableFeatures = computed(() =>
    this.features.filter(f => {
      if (!f.welcomeCmpVisible) return true;

      const isVisibleSignal = untracked(() =>
        runInInjectionContext(this.injector, () => f.welcomeCmpVisible!()),
      );
      return isVisibleSignal();
    }),
  );
  protected firstName = computed(() => this.userFullName().split(' ')[0] || '');
  protected activeFeature = signal<HRAssistantFeature | null>(null);

  public ngOnInit() {
    this.scrollToBottom(true);
    const restoreLastThread = !this.hrAssistantChatService.isNewThread();
    this.hrAssistantChatService
      .loadThreads(restoreLastThread)
      .then(() => this.scrollToBottom(true));
  }

  public clearFeature() {
    this.activeFeature.set(null);
  }

  public setFeatureById(featureId: string) {
    const feature = this.features.find(f => f.id === featureId) || null;
    this.activeFeature.set(feature);
  }

  protected markdownToHtml = (markdownText: string) => marked(markdownText, { async: false });

  protected toggleExpandContext(messageId: string) {
    this.contextExpandedMap.update(obj => ({ ...obj, [messageId]: !obj[messageId] }));
  }

  protected onClose() {
    this.close.emit();
  }

  protected onNewThread() {
    this.hrAssistantChatService.isNewThread.set(true);
    this.hrAssistantChatService.startNewThread();
  }

  protected async onSend() {
    const value = this.chatForm.value.trim();
    this.chatForm.setValue('');
    this.scrollToBottom();
    await this.hrAssistantChatService.sendMessage(value);
    this.hrAssistantAnalytics.trackQuestionAsked(false, this.currentThreadId());
    this.scrollToBottom();
  }

  protected async onStarterClick(staterValue: string) {
    this.chatForm.setValue('');
    await this.hrAssistantChatService.sendMessage(staterValue);
    this.hrAssistantAnalytics.trackQuestionAsked(true, this.currentThreadId());
    this.scrollToBottom();
  }

  protected onFeedback(event: 'up' | 'down' | null) {
    if (!event) return;

    this.hrAssistantAnalytics.responseRated(event, this.currentThreadId());
  }

  protected onFeatureClick(feature: HRAssistantFeature) {
    this.activeFeature.set(feature);
  }

  protected getToolInvocationResultText(toolInvocations: ToolInvocation[]): AiResponseContext[] {
    if (!toolInvocations) return [];

    return toolInvocations.reduce((acc, invocation) => {
      if (invocation.toolName === 'vectorQueryTool') {
        const result = <VectorQueryToolResult>(<any>invocation).result;
        const filteredSources = result.sources.filter(
          source => source.score > SOURCE_SCORE_THRESHOLD,
        );
        const relevantSources =
          filteredSources.length === 0 && result.sources.length > 0
            ? [
                result.sources.reduce((topSource, source) =>
                  source.score > topSource.score ? source : topSource,
                ),
              ]
            : filteredSources;
        return [
          ...acc,
          ...relevantSources.map(context => ({
            title: context.metadata.title,
            url: context.metadata.url,
            text: `"${context.metadata.text}"`,
            icon: context.metadata.provider_id,
          })),
        ];
      }
      return acc;
    }, []);
  }

  protected getMessageErrorButtons = (message: AiMessage): InfoboxButton[] => {
    if (!message.isError) return [];

    return [
      {
        text: this.i18n.translate('tlc.common.retry'),
        variant: 'outline-error',
        actionFunction: () => this.hrAssistantChatService.retry(message),
      },
    ];
  };

  private scrollToBottom(instant = false) {
    if (this.destroyRef.destroyed) return;
    afterNextRender(
      () => {
        const container = this.messagesContainer()?.nativeElement;
        const lastChild = <HTMLElement | undefined>container?.lastElementChild;
        if (!container || !lastChild) return;

        lastChild.scrollIntoView({
          block: 'start',
          behavior: instant ? 'instant' : 'smooth',
        });
      },
      { injector: this.injector },
    );
  }
}
